/**
 * Enchanting table screen (vanilla EnchantmentScreen layout, 176x166):
 * item slot (15,47), lapis slot (35,47), three option buttons 108x19 at
 * (60, 14 + 19*i) with the level icon, a clue line and the green level cost.
 * Costs and rolls come from game/enchant/table.ts with the player's
 * enchantment seed; enchanting spends (slot+1) levels and lapis and rerolls the seed.
 */

import { enchName } from '../../game/enchant/stack';
import { CLICK, enchantMenu, type EnchantMenu } from '../../game/menu';
import { EnchantNames, sgaReady, sgaText } from '../sgaText';
import { isEmpty } from '../../game/types';
import { GuiStage, Label, sheet, sprite, uiSound } from '../kit';
import { ContainerScreen } from './container';
import type { ScreenDeps } from './screenHost';

export interface EnchantCtx {
  bookshelves: number;
  /** Player XP level. */
  level: () => number;
  /** Take `n` levels (survival). */
  spendLevels: (n: number) => void;
  /** Player enchantment seed (vanilla xpSeed). */
  seed: () => number;
  rerollSeed: () => void;
}

const BTN_X = 60, BTN_Y = 14, BTN_W = 108, BTN_H = 19;

export class EnchantScreen extends ContainerScreen {
  private readonly table: EnchantMenu;
  private get costs(): [number, number, number] { return this.table.costs; }
  private readonly btns: HTMLDivElement[] = [];
  private readonly icons: HTMLDivElement[] = [];
  private readonly costLabels: Label[] = [];
  private readonly clueEls: HTMLDivElement[] = [];
  private readonly names = new EnchantNames();
  private lastKey = '';

  constructor(deps: ScreenDeps, stage: GuiStage, private readonly ctx: EnchantCtx) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/enchanting_table.png', 0, 0, 176, 166, 0, 0));
    for (let i = 0; i < 3; i++) {
      const b = sprite('gui/sprites/container/enchanting_table/enchantment_slot_disabled.png', BTN_X, BTN_Y + BTN_H * i, BTN_W, BTN_H);
      const ic = sprite('gui/sprites/container/enchanting_table/level_1_disabled.png', BTN_X + 1, BTN_Y + BTN_H * i + 1, 16, 16);
      const clue = document.createElement('div');
      clue.style.cssText = `position:absolute;left:${BTN_X + 20}px;top:${BTN_Y + BTN_H * i + 2}px;pointer-events:none;`;
      const cost = new Label('', BTN_X + BTN_W - 2, BTN_Y + BTN_H * i + 9, { color: '#80ff20', shadow: true, align: 'right' });
      this.root.append(b, ic, clue, cost.el);
      this.btns.push(b); this.icons.push(ic); this.clueEls.push(clue); this.costLabels.push(cost);
    }
    // 0 item, 1 lapis, 2..28 main, 29..37 hotbar
    this.table = this.use(enchantMenu(this.env({ level: ctx.level, spendLevels: ctx.spendLevels }),
      { bookshelves: () => ctx.bookshelves, seed: ctx.seed, rerollSeed: ctx.rerollSeed }));
    this.table.onOffers = () => { this.lastKey = ''; if (this.btns.length) this.paintButtons(); };
    this.at(0, 15, 47);
    this.at(1, 35, 47, { emptyIcon: 'gui/sprites/container/slot/lapis_lazuli.png' });
    this.placePlayer(2, 84, 142);
    this.addLabel('Enchant', 12, 5);
    this.addLabel('Inventory', 8, 72);
    this.mount();
    this.table.recompute();
  }

  private get creative(): boolean { return this.deps.gameMode() === 'creative'; }

  private usable(i: number): boolean { return this.table.usable(i); }

  private paintButtons(): void {
    const key = `${this.costs.join(',')}|${this.ctx.level()}|${this.table.lapis.count}|${this.hoverBtn()}|${sgaReady()}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    const hb = this.hoverBtn();
    // vanilla: the galactic words are re-seeded with the enchant seed every frame, 3-4 words per slot
    this.names.initSeed(this.ctx.seed());
    for (let i = 0; i < 3; i++) {
      const c = this.costs[i];
      const ok = this.usable(i);
      const base = 'gui/sprites/container/enchanting_table/';
      const state = c <= 0 || !ok ? 'enchantment_slot_disabled' : hb === i ? 'enchantment_slot_highlighted' : 'enchantment_slot';
      this.btns[i].style.backgroundImage = `url(mc/${base}${state}.png)`;
      this.icons[i].style.display = c > 0 ? '' : 'none';
      this.icons[i].style.backgroundImage = `url(mc/${base}level_${i + 1}${ok ? '' : '_disabled'}.png)`;
      this.costLabels[i].set(c > 0 ? String(c) : '');
      this.costLabels[i].setColor(ok ? '#80ff20' : '#407f10');
      const words = c > 0 ? this.names.next(86 - String(c).length * 6) : '';
      this.clueEls[i].replaceChildren();
      if (words) this.clueEls[i].appendChild(sgaText(words, ok ? (hb === i ? '#ffff80' : '#685e4a') : '#342f25'));
    }
  }

  /** The slot's clue enchantment (vanilla tooltip "Sharpness III . . . ?"). */
  private clue(i: number): string {
    const e = this.table.clues[i];
    return e ? enchName(e.id, e.lvl) : '';
  }

  private hoverBtn(): number {
    const lx = this.mx - this.left, ly = this.my - this.top;
    if (lx < BTN_X || lx >= BTN_X + BTN_W) return -1;
    const i = Math.floor((ly - BTN_Y) / BTN_H);
    return ly >= BTN_Y && i >= 0 && i < 3 ? i : -1;
  }

  override pointerDown(button: number, shift: boolean): void {
    const b = this.hoverBtn();
    if (b >= 0 && button === 0 && isEmpty(this.cursor)) { this.enchant(b); return; }
    super.pointerDown(button, shift);
  }

  private enchant(i: number): void {
    if (!this.table.button!(i)) return;
    this.report(null, i, CLICK.BUTTON);
    uiSound.play?.('block.enchantment_table.use');
    this.changed();
  }

  protected override tipLines(): string[] | null {
    const b = this.hoverBtn();
    if (b >= 0 && this.costs[b] > 0) {
      const clue = this.clue(b);
      const lines = [`${clue} . . . ?`];
      if (!this.creative) {
        if (this.ctx.level() < this.costs[b]) lines.push(`§cEnchantment Level Requirement: ${this.costs[b]}`);
        else {
          lines.push(`${b + 1} Lapis Lazuli`);
          lines.push(`${b + 1} Enchantment Level${b ? 's' : ''}`);
        }
      }
      return lines;
    }
    return super.tipLines();
  }

  override update(): void { this.paintButtons(); }
}
